import * as React from 'react';
import { StyleSheet, css } from 'aphrodite/no-important';
import { TYPOGRAPHY_SIZE } from 'mm-theme-configuration/dist/consts';
import { useTheme } from '../../../theming/useTheme';
import { Image } from '../../partials/image/Image';
import { MEDIA_BREAKPOINTS } from '../../../mediaQueries.const';
import { ErrorBoundary } from '../../errorHandling/ErrorBoundary';
import { AdditionalStyleSheet } from '../../components.utils';
import { Thumbnail } from '../../partials/image/image.types';
import { Link } from '../../partials/link/Link';
import { H1, H4, P, Span, semanticCSS } from '../../../typography/semanticTags';
import { BaseUnit } from '../../../theming/baseUnitDefinition';
import { createImageWidths } from '../../partials/image/image.utils';
import { TranslatedFontSize } from '../../../theming/fontSizeTranslator';
import { createStyleForBoxShadow } from '../../../theming/boxShadow';
import { getStyleBySize } from '../../../typography/semanticTags/semanticTags.utils';

const componentConfigThemeKey = 'errorPage';

interface ErrorComponentProps {
  image: Thumbnail;
  title: string;
  secondaryTitle: string;
  content: string;
  button: {
    text: string;
    href: string;
  };
}

interface ErrorComponentThemeProps {
  backgroundColorNormal: string;
  backgroundColorHover: string;
  backgroundColorActive: string;
  buttonTextColorNormal: string;
  buttonTextColorHover: string;
  buttonTextColorActive: string;
  borderRadius: number;
  borderWidthNormal: number;
  borderWidthHover: number;
  borderWidthActive: number;
  borderColorNormal: string;
  borderColorHover: string;
  borderColorActive: string;
  boxShadowTypeNormal: string;
  boxShadowTypeHover: string;
  boxShadowTypeActive: string;
  baseUnit: BaseUnit;
  fontSizesStyles: TranslatedFontSize;
  titleColor: string;
  textColor: string;
}

const createErrorStyle = (theme: ErrorComponentThemeProps) => {
  const { baseUnit,
    titleColor,
    textColor,
    buttonTextColorNormal,
    buttonTextColorHover,
    buttonTextColorActive,
    backgroundColorNormal,
    backgroundColorHover,
    backgroundColorActive,
    borderRadius } = theme;
  return StyleSheet.create({
    title: {
      textAlign: 'center',
      color: titleColor,
    },
    secondaryTitle: {
      textAlign: 'center',
      color: titleColor,
      [MEDIA_BREAKPOINTS.large]: {
        marginTop: 0,
      },
      [MEDIA_BREAKPOINTS.medium]: {
        marginTop: `${baseUnit / 2}px`,
      },
      [MEDIA_BREAKPOINTS.small]: {
        marginTop: `${baseUnit / 2}px`,
      },
    },
    content: {
      textAlign: 'center',
      color: textColor,
    },
    buttonWrapper: {
      display: 'flex',
      justifyContent: 'center',
      textAlign: 'center',
      margin: `${baseUnit}px 0`,
    },
    button: {
      display: 'flex',
      height: '40px',
      paddingRight: `${baseUnit}px`,
      paddingLeft: `${baseUnit}px`,
      alignItems: 'center',
      color: buttonTextColorNormal,
      ':hover': {
        backgroundColor: backgroundColorHover,
        color: buttonTextColorHover,
      },
      ':active': {
        backgroundColor: backgroundColorActive,
        color: buttonTextColorActive,
      },
      backgroundColor: backgroundColorNormal,
      borderRadius: `${borderRadius}px`,
      textDecoration: 'none',
    },
  });
};

const createImageErrorStyle = (theme: ErrorComponentThemeProps) => {
  const { baseUnit } = theme;
  return StyleSheet.create({
    style: {
      margin: `${baseUnit * 2}px auto ${baseUnit}px auto`,
      [MEDIA_BREAKPOINTS.large]: {
        width: '300px',
        height: '300px',
      },
      [MEDIA_BREAKPOINTS.medium]: {
        width: '180px',
        height: '180px',
      },
      [MEDIA_BREAKPOINTS.small]: {
        width: '180px',
        height: '180px',
      },
    },
  });
};

const imageWidths = createImageWidths(360, 360, 360);

export const Error: React.FunctionComponent<ErrorComponentProps> = props => {
  const { title, image, secondaryTitle, content, button } = props;
  const themeProps = useTheme<ErrorComponentThemeProps>(componentConfigThemeKey);
  const aspectRatio = { x: 1, y: 1 };
  const errorStyle = createErrorStyle(themeProps);
  const imageErrorStyle = createImageErrorStyle(themeProps) as AdditionalStyleSheet;
  const { fontSizesStyles, boxShadowTypeNormal, boxShadowTypeHover, boxShadowTypeActive, borderColorNormal, borderColorActive, borderColorHover, borderWidthNormal, borderWidthActive, borderWidthHover } = themeProps;
  const borderBoxShadow = {
    borderColorTypeActive: borderColorActive,
    borderColorTypeHover: borderColorHover,
    borderColorTypeNormal: borderColorNormal,
    borderWidthTypeActive: borderWidthActive,
    borderWidthTypeHover: borderWidthHover,
    borderWidthTypeNormal: borderWidthNormal,
  };
  const boxShadowStyle = createStyleForBoxShadow({ boxShadowTypeNormal, boxShadowTypeHover, boxShadowTypeActive, borderBoxShadow });
  const textStyle = getStyleBySize(fontSizesStyles, { fontSizeSmall: TYPOGRAPHY_SIZE.BIGGER, fontSizeMedium: TYPOGRAPHY_SIZE.BIGGER, fontSizeLarge: TYPOGRAPHY_SIZE.BIGGER });

  return (
    <ErrorBoundary>
      <div className={css(imageErrorStyle.style)}>
        <Image {...image} additionalStyle={imageErrorStyle} aspectRatio={aspectRatio} imageWidths={imageWidths} transparent />
      </div>
      <H1 styles={semanticCSS(errorStyle.title)}>{title}</H1>
      <H4 styles={semanticCSS(errorStyle.secondaryTitle)}>{secondaryTitle}</H4>
      <P styles={semanticCSS(errorStyle.content)}>{content}</P>
      <div className={css(errorStyle.buttonWrapper)}>
        <Link href={button.href} className={css(errorStyle.button, boxShadowStyle.style, textStyle)}><Span>{button.text}</Span></Link>
      </div>
    </ErrorBoundary>
  );
};
